import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useRegions, useDepartements } from '@/hooks/useGeography';
import type { CandidateFilters, SeniorityLevel, AvailabilityStatus, CandidateStatus } from '../types';

interface CandidatesFiltersProps {
  filters: CandidateFilters;
  onFiltersChange: (filters: CandidateFilters) => void;
}

const seniorityOptions: { value: SeniorityLevel; label: string }[] = [
  { value: 'junior', label: 'Junior' },
  { value: 'mid', label: 'Confirmé' },
  { value: 'senior', label: 'Senior' },
  { value: 'expert', label: 'Expert' },
  { value: 'lead', label: 'Lead' },
];

const availabilityOptions: { value: AvailabilityStatus; label: string }[] = [
  { value: 'available', label: 'Disponible immédiatement' },
  { value: 'open', label: 'Ouvert aux opportunités' },
  { value: 'not_looking', label: 'Non disponible' },
];

const statusOptions: { value: CandidateStatus; label: string }[] = [
  { value: 'active', label: 'Actif' },
  { value: 'archived', label: 'Archivé' },
  { value: 'blacklisted', label: 'Blacklisté' },
];

const contractOptions: { value: string; label: string }[] = [
  { value: 'cdi', label: 'CDI' },
  { value: 'freelance', label: 'Freelance' },
  { value: 'both', label: 'Les deux' },
];

export function CandidatesFilters({ filters, onFiltersChange }: CandidatesFiltersProps) {
  const { data: regions = [] } = useRegions();
  const { data: departements = [] } = useDepartements();

  // Filtrer les départements par région si une région est sélectionnée
  const filteredDepartements = filters.regionId
    ? departements.filter((d) => d.regionId === filters.regionId)
    : departements;

  const handleSearchChange = (value: string) => {
    onFiltersChange({ ...filters, search: value || undefined });
  };

  const handleSelectChange = (key: keyof CandidateFilters) => (value: string) => {
    onFiltersChange({ ...filters, [key]: value === 'all' ? undefined : value });
  };

  const handleRegionChange = (value: string) => {
    if (value === 'all') {
      onFiltersChange({ ...filters, regionId: undefined, departementId: undefined });
    } else {
      const regionId = parseInt(value);
      // Si le département actuel n'appartient pas à la nouvelle région, le réinitialiser
      const currentDept = departements.find((d) => d.id === filters.departementId);
      const newFilters: CandidateFilters = { ...filters, regionId };
      if (currentDept && currentDept.regionId !== regionId) {
        newFilters.departementId = undefined;
      }
      onFiltersChange(newFilters);
    }
  };

  const handleDepartementChange = (value: string) => {
    if (value === 'all') {
      onFiltersChange({ ...filters, departementId: undefined });
    } else {
      const deptId = parseInt(value);
      const dept = departements.find((d) => d.id === deptId);
      const newFilters: CandidateFilters = { ...filters, departementId: deptId };
      // Auto-sélectionner la région si pas encore choisie
      if (dept?.regionId && !filters.regionId) {
        newFilters.regionId = dept.regionId;
      }
      onFiltersChange(newFilters);
    }
  };

  return (
    <div className="space-y-4">
      {/* Ligne 1 : Recherche + filtres principaux */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher un candidat..."
            value={filters.search || ''}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <Select value={filters.status || 'all'} onValueChange={handleSelectChange('status')}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              {statusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filters.seniority || 'all'} onValueChange={handleSelectChange('seniority')}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Séniorité" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes séniorités</SelectItem>
              {seniorityOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filters.availability || 'all'} onValueChange={handleSelectChange('availability')}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Disponibilité" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes dispos</SelectItem>
              {availabilityOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filters.contractPreference || 'all'} onValueChange={handleSelectChange('contractPreference')}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Contrat" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous contrats</SelectItem>
              {contractOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Ligne 2 : Filtres géographiques */}
      <div className="flex flex-wrap gap-2">
        <Select value={filters.regionId?.toString() || 'all'} onValueChange={handleRegionChange}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Région" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes régions</SelectItem>
            {regions.map((r) => (
              <SelectItem key={r.id} value={r.id.toString()}>
                {r.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          key={`dept-${filteredDepartements.length}`}
          value={filters.departementId?.toString() || 'all'}
          onValueChange={handleDepartementChange}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Département" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous départements</SelectItem>
            {filteredDepartements.map((d) => (
              <SelectItem key={d.id} value={d.id.toString()}>
                {d.code} - {d.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
